import { animate, createTimeline, utils } from 'animejs';

const FORM_SELECTOR = '.newsletter-form';

// ─── Entrance ────────────────────────────────────────────────────────────────

function initEntranceAnimation() {
    const form = document.querySelector(FORM_SELECTOR);
    if (!form) return;

    const title    = form.querySelector('.title');
    const subtitle = form.querySelector('.subtitle');
    const body     = form.querySelector('p.text-white');
    const input    = form.querySelector('.form-input');
    const btnMob   = form.querySelector('.btn.d-lg-none');
    const check    = form.querySelector('.form-check');
    const small    = form.querySelector('.small');

    const els = [title, subtitle, body, input, btnMob, check, small].filter(Boolean);

    // Set initial state
    els.forEach(el => utils.set(el, { opacity: 0, translateY: 60 }));

    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            runEntranceTimeline({ title, subtitle, body, input, btnMob, check, small });
        });
    }, { threshold: 0.15 });

    observer.observe(form);
}

function runEntranceTimeline({ title, subtitle, body, input, btnMob, check, small }) {
    const tl = createTimeline({ ease: 'outExpo', duration: 750 });

    if (title)    tl.add(title,    { opacity: [0, 1], translateY: [60, 0], scale: [0.9, 1] }, 0);
    if (subtitle) tl.add(subtitle, { opacity: [0, 1], translateY: [50, 0], scale: [0.92, 1] }, 160);
    if (body)     tl.add(body,     { opacity: [0, 1], translateY: [40, 0] }, 320);
    if (input)    tl.add(input,    { opacity: [0, 1], translateY: [40, 0], scaleX: [0.94, 1], duration: 850, ease: 'outBack(1.2)' }, 460);
    if (btnMob)   tl.add(btnMob,   { opacity: [0, 1], translateY: [30, 0], scaleY: [0.85, 1], ease: 'outBack(1.5)' }, 580);
    if (check)    tl.add(check,    { opacity: [0, 1], translateY: [25, 0] }, 680);
    if (small)    tl.add(small,    { opacity: [0, 1], translateY: [20, 0] }, 780);
}

// ─── Input focus / blur ───────────────────────────────────────────────────────

function initInputAnimation() {
    const form = document.querySelector(FORM_SELECTOR);
    if (!form) return;

    const inputWrap = form.querySelector('.form-input');
    const input     = form.querySelector('#email');
    if (!inputWrap || !input) return;

    input.addEventListener('focus', () => {
        animate(inputWrap, {
            scaleX: 1.03,
            scaleY: 1.06,
            duration: 320,
            ease: 'outBack(2)',
        });
    });

    input.addEventListener('blur', () => {
        animate(inputWrap, {
            scaleX: 1,
            scaleY: 1,
            duration: 400,
            ease: 'outQuart',
        });
    });
}

// ─── Submit button pulse ──────────────────────────────────────────────────────

function initButtonAnimation() {
    const form = document.querySelector(FORM_SELECTOR);
    if (!form) return;

    form.querySelectorAll('[type="submit"]').forEach(btn => {
        btn.addEventListener('mouseenter', () => {
            animate(btn, {
                scale: 1.09,
                duration: 280,
                ease: 'outBack(2)',
            });
        });

        btn.addEventListener('mouseleave', () => {
            animate(btn, {
                scale: 1,
                duration: 350,
                ease: 'outElastic(1, .6)',
            });
        });
    });
}

// ─── Success message ─────────────────────────────────────────────────────────

/**
 * Called externally (from newsletter.js) right after the done-message
 * is shown and the form-input is hidden, so we can animate the transition.
 *
 * @param {HTMLElement} doneMsg  — the .done-message element
 * @param {HTMLElement} formInput — the .form-input element
 */
export function animateSuccess(doneMsg, formInput) {
    // Slam the input row out
    animate(formInput, {
        opacity: [1, 0],
        translateY: [0, -30],
        scaleY: [1, 0.8],
        duration: 380,
        ease: 'inBack(2)',
    });

    // Spring the success message in
    utils.set(doneMsg, { opacity: 0, translateY: 40, scale: 0.88 });
    animate(doneMsg, {
        opacity: [0, 1],
        translateY: [40, 0],
        scale: [0.88, 1],
        duration: 600,
        ease: 'outBack(1.8)',
        delay: 280,
    });
}

/**
 * Called after the done-message timeout to fade it out and bring the
 * form-input back.
 *
 * @param {HTMLElement} doneMsg   — the .done-message element
 * @param {HTMLElement} formInput — the .form-input element
 */
export function animateRestore(doneMsg, formInput) {
    // Fade the message out
    animate(doneMsg, {
        opacity: [1, 0],
        translateY: [0, -20],
        scale: [1, 0.92],
        duration: 350,
        ease: 'inBack(1.5)',
        onComplete: () => doneMsg.classList.add('d-none'),
    });

    // Spring the input row back in
    animate(formInput, {
        opacity: [0, 1],
        translateY: [-30, 0],
        scaleY: [0.8, 1],
        duration: 550,
        ease: 'outBack(1.4)',
        delay: 200,
    });
}

// ─── Init ─────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
    initEntranceAnimation();
    initInputAnimation();
    initButtonAnimation();
});
